// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mapServerMessages.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";

import { type AskV2ThreadMessage, type AskV2ThreadStatus } from "./types";

const isReviewStatusMessage = (message: AskPunttMessage): boolean =>
  message.role === "assistant" &&
  message.content === "" &&
  (message.agentSteps?.length ?? 0) > 0 &&
  (message.agentSteps ?? []).every(
    (step) =>
      (step as { tool?: string; type?: string }).tool === "thought" ||
      (step as { tool?: string; type?: string }).type === "text",
  );

const isEmptyAssistantPlaceholder = (message: AskPunttMessage): boolean => {
  if (message.role !== "assistant" || isReviewStatusMessage(message)) {
    return false;
  }
  return (
    !(message.content || message.streamingContent) &&
    !(message.agentSteps?.length ?? 0) &&
    !(message.searchProgress?.length ?? 0) &&
    !(message.contentBlocks?.length ?? 0)
  );
};

const shouldHideEmptyAssistantPlaceholder = ({
  message,
  isLast,
}: {
  message: AskPunttMessage;
  isLast: boolean;
}): boolean => {
  if (!isEmptyAssistantPlaceholder(message)) return false;
  // why: keep the live tail so PunttThinking / poll can see an in-flight turn
  return !isLast;
};

function mapAssistantStatus(message: AskPunttMessage): AskV2ThreadStatus {
  if (message.status === "pending") return "streaming";
  // why: completed/errored empty tails are terminal — do not keep history polling
  if (message.status === "completed") return "sent";
  if (
    !message.content &&
    !message.streamingContent &&
    !isReviewStatusMessage(message)
  ) {
    return "streaming";
  }
  return "sent";
}

function mapServerMessage(message: AskPunttMessage): AskV2ThreadMessage {
  const content = message.streamingContent ?? message.content ?? "";
  if (message.role === "user") {
    return {
      clientId: message._id,
      serverId: message._id,
      role: "user",
      content,
      status: "sent",
      createdAt: message.createdAt,
      userId: message.userId,
      quotedContexts: message.quotedContexts,
      commentContexts: message.commentContexts,
      openItemContexts: message.openItemContexts,
      mentions: message.mentions,
      mentionEmails: message.mentionEmails,
    };
  }

  return {
    clientId: message._id,
    serverId: message._id,
    role: "assistant",
    content,
    status: mapAssistantStatus(message),
    createdAt: message.createdAt,
  };
}

export function mapServerMessages(
  messages: AskPunttMessage[],
  _presentation: "chat" | "feed" = "chat",
): AskV2ThreadMessage[] {
  const visible = messages.filter((message) => !message.hidden);
  const mapped: AskV2ThreadMessage[] = [];

  visible.forEach((message, index) => {
    const isLast = index === visible.length - 1;
    if (shouldHideEmptyAssistantPlaceholder({ message, isLast })) {
      return;
    }
    mapped.push(mapServerMessage(message));
  });

  return mapped;
}

export function hasStreamingServerMessages(
  messages: AskV2ThreadMessage[],
): boolean {
  return messages.some((message) => message.status === "streaming");
}

/**
 * Warhol answers whole on history, with no pending assistant row. Poll while
 * the last visible turn is still the user (or a rembrandt stream is live).
 */
export function shouldPollAskV2History(
  messages: AskV2ThreadMessage[],
  inFlight: boolean,
): boolean {
  if (inFlight) return true;
  if (hasStreamingServerMessages(messages)) return true;
  return messages[messages.length - 1]?.role === "user";
}

export function rawHistoryNeedsPoll(
  messages: AskPunttMessage[],
  inFlight: boolean,
): boolean {
  if (inFlight) return true;
  const visible = messages.filter((message) => !message.hidden);
  if (
    visible.some(
      (message) => message.role === "assistant" && message.status === "pending",
    )
  ) {
    return true;
  }
  return visible[visible.length - 1]?.role === "user";
}

/**
 * Whether a history payload is about the turn the client is waiting on.
 *
 * `pendingTurn` is the server's word about the transcript *it* returned, and
 * `nextAskV2HistoryPollMs` acts on it as if it were about the turn the person is
 * waiting for. Straight after a send it is not: the newest payload still ends on
 * the PREVIOUS turn, and its `pendingTurn: false` means "the turn in this
 * transcript is over", not "no turn will run".
 *
 * Read the second way it cleared the refetch interval, the query never refetched
 * again, and the tab that clicked Start review sat on the preflight card for a
 * whole review — 0 rail rows, 0 tool rows, 0 findings — while a fresh tab on the
 * same chat drew everything. It had no socket either, and for the same reason:
 * `streamMessageId` is derived from that same stale transcript, so the
 * subscription never armed.
 *
 * A payload is about the local turn when the last person's message it carries is
 * the one the local thread is waiting on. A row the ACK has not named yet can
 * match no server id, so it never reads as seen — "not seen" only keeps the poll
 * on, which is the safe direction.
 */
export function historyPayloadCoversLocalTurn(
  serverMessages: readonly AskPunttMessage[] | undefined,
  localMessages: readonly AskV2ThreadMessage[],
): boolean {
  const localTurn = lastUserRow(localMessages);
  // Nothing local to wait on: the payload is the only word there is.
  if (!localTurn) return true;
  // A failed or aborted send is terminal, not outstanding — and it is kept in
  // the local tail forever, so it must not hold the poll open.
  if (localTurn.status === "failed" || localTurn.status === "aborted") {
    return true;
  }
  const serverTurn = lastUserRow(serverMessages ?? []);
  return !!localTurn.serverId && serverTurn?._id === localTurn.serverId;
}

/** The newest row written by a person, or undefined when there is none. */
function lastUserRow<T extends { role: string }>(
  rows: readonly T[],
): T | undefined {
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const row = rows[index];
    if (row?.role === "user") return row;
  }
  return undefined;
}

export const HISTORY_POLL_MS = 2000;
// The history poll while the WebSocket is carrying the turn.
//
// The thread used to come off this poll at 2s because the poll was the only
// thing that moved it. It is not any more: the socket delivers the running
// turn's tool rows, its answer text and its checks (`applyStreamSnapshot`), so
// while pushes are landing the poll is a SAFETY NET — it is what catches a
// frame the socket dropped, and it is still the only carrier of the settled
// answer, which is why it never stops outright. 20s bounds the cost of a lost
// frame without being slow enough to notice next to a review of minutes.
//
// Restored to `HISTORY_POLL_MS` the moment the socket goes quiet, on the same
// 20s stall guard the hook itself uses (`PUSH_STALL_MS`).
export const HISTORY_POLL_SOCKET_MS = 20_000;
export const HISTORY_POLL_MAX_MS = 2 * 60 * 1000;
// How long to wait before re-reading history after a read that FAILED.
//
// A failed read says nothing about the turn: a network blip, one 5xx, a Lambda
// cold-start timeout. Returning `false` for it is the refetch interval for
// "never ask again", so one blip froze the thread until the person reloaded —
// the exact complaint this poll exists to answer. Bounded doubling from 2s,
// capped so a backend that stays down is asked at ~4 rpm rather than four times
// a second. Nothing has to undo it on recovery: the next success is not an
// error and takes the normal cadence again.
export const HISTORY_POLL_ERROR_BASE_MS = 2_000;
export const HISTORY_POLL_ERROR_MAX_MS = 15_000;

/** The retry wait after `errorCount` consecutive failed reads (1-based). */
export function historyErrorPollMs(errorCount: number): number {
  const step = Math.max(errorCount, 1) - 1;
  return Math.min(
    HISTORY_POLL_ERROR_BASE_MS * 2 ** step,
    HISTORY_POLL_ERROR_MAX_MS,
  );
}
// why: a warhol artwork review is one turn of 5-15 minutes; the rembrandt
// 2-minute cap would stop the history poll that IS the delivery.
export const WARHOL_HISTORY_POLL_MAX_MS = 30 * 60 * 1000;

export function nextAskV2HistoryPollMs(input: {
  queryStatus: string;
  messages: AskPunttMessage[];
  inFlight: boolean;
  pollStartedAt: number | null;
  now: number;
  agent?: string | null;
  /**
   * What the server says about the turn this chat is waiting on. `undefined`
   * when it did not say — an older response, a non-warhol chat, or a read that
   * could not ask.
   */
  pendingTurn?: boolean;
  /**
   * Whether the server has confirmed THIS turn is running at any point in this
   * poll. Latched by the caller and fed back in, so one unanswered read cannot
   * undo every answered one before it.
   */
  pendingTurnSeen?: boolean;
  /**
   * How many reads have failed in a row, derived by the caller as
   * `query.state.errorUpdateCount` minus the value it held at the last
   * SUCCESS. It sizes the retry backoff, and it is read from query state rather
   * than counted here because `refetchInterval` is evaluated more than once per
   * fetch — a counter of this function's own would advance on a re-render and
   * back off a healthy poll.
   *
   * NOT `query.state.fetchFailureCount`: with `retry: false` that reads 1 after
   * every failed fetch however long the backend has been down, which pins the
   * interval at the 2s base and makes `HISTORY_POLL_ERROR_MAX_MS` unreachable.
   * `historyErrorBaselineRef` in `useAskV2Transport` carries the same warning.
   */
  errorCount?: number;
  /**
   * Whether ANY payload has been read for this chat. It is what tells "the
   * last read said the turn is over" apart from "no read has ever succeeded":
   * with no payload there is no transcript to read a stop condition off, and
   * stopping there is the frozen empty thread the person reloads out of.
   */
  hasPayload?: boolean;
}): {
  interval: number | false;
  pollStartedAt: number | null;
  pendingTurnSeen: boolean;
} {
  if (input.queryStatus === "error") {
    // A read that could not ask is not a turn that ended. Retry on the bounded
    // backoff, and carry the poll's own state across the failure — the same
    // rule the cap already runs on, that an unanswered read must not be the one
    // that forgets what an answered one said.
    //
    // BOTH stop conditions still hold here, exactly as they do on the success
    // path. The first is the payload's: a transcript that says nothing is
    // outstanding has nothing to recover. The second is the server's own word
    // that a warhol turn is over — a read that failed afterwards cannot un-say
    // it, and dropping it here is what would poll a finished turn to the
    // 30-minute cap. This branch used to apply only the payload's stop while
    // its comment claimed parity with the success path.
    const nothingOutstanding =
      input.hasPayload === true &&
      !rawHistoryNeedsPoll(input.messages, input.inFlight);
    const serverSaysTurnIsOver =
      input.pendingTurn === false && input.agent === "warhol";
    if (nothingOutstanding || serverSaysTurnIsOver) {
      return { interval: false, pollStartedAt: null, pendingTurnSeen: false };
    }
    return {
      interval: historyErrorPollMs(input.errorCount ?? 1),
      pollStartedAt: input.pollStartedAt,
      pendingTurnSeen: input.pendingTurnSeen === true,
    };
  }
  if (!rawHistoryNeedsPoll(input.messages, input.inFlight)) {
    return { interval: false, pollStartedAt: null, pendingTurnSeen: false };
  }
  const pollStartedAt = input.pollStartedAt ?? input.now;
  const seen = input.pendingTurnSeen === true || input.pendingTurn === true;
  // The turn is over on the server's own word, and the transcript still looks
  // like it is waiting (a turn that failed with no answer leaves the person's
  // message last forever). Stop now rather than polling a dead turn to the cap.
  if (input.pendingTurn === false && input.agent === "warhol") {
    return { interval: false, pollStartedAt, pendingTurnSeen: false };
  }
  // The server says the turn is still running. `WARHOL_HISTORY_POLL_MAX_MS`
  // is a guess at how long a review takes, and a review that outruns the guess
  // freezes mid-rail for a person who stayed — the exact failure this story
  // exists to remove. While the server is willing to say "still running", the
  // answer is worth waiting for however long it takes.
  if (input.pendingTurn === true) {
    return { interval: HISTORY_POLL_MS, pollStartedAt, pendingTurnSeen: true };
  }
  const maxMs =
    input.agent === "warhol" ? WARHOL_HISTORY_POLL_MAX_MS : HISTORY_POLL_MAX_MS;
  if (input.now - pollStartedAt >= maxMs) {
    if (input.agent === "warhol") {
      // ABSENT is not "over" — it is "this read could not ask" (no session, or
      // Temporal unreachable). Once the server has confirmed this turn is
      // running, a single unanswered read must not be the one that ends the
      // poll: at minute 31 of a 35-minute review, one blip would freeze the
      // rail for the person who stayed, which is the whole failure this
      // replaces. So the cap only stops a warhol poll that has NEVER been
      // told anything — the server that cannot answer at all, which is what
      // the cap was ever for.
      return seen
        ? { interval: HISTORY_POLL_MS, pollStartedAt, pendingTurnSeen: true }
        : { interval: false, pollStartedAt, pendingTurnSeen: false };
    }
    // why: keep polling long reviews — roll the budget instead of freezing off.
    return {
      interval: HISTORY_POLL_MS,
      pollStartedAt: input.now,
      pendingTurnSeen: seen,
    };
  }
  return { interval: HISTORY_POLL_MS, pollStartedAt, pendingTurnSeen: seen };
}
